import React, { useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Segment,
  Table,
  Message,
  Label,
  Form,
  Grid,
  Button,
  Icon,
  Dropdown,
  Input,
  Header,
  Pagination,
  Loader,
} from "semantic-ui-react";
import { useExport } from "./export";
import { CONFIG } from "./config";
import { useUserProfiles, useProcessedSessions, useFilters } from "./hooks";

const UserSessionTable = ({ allSessions = [], hourlyPeakData = {} }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [exportValue, setExportValue] = useState(null);
  const processedSessions = useProcessedSessions(allSessions);
  const { userProfiles, loadingProfiles } = useUserProfiles(processedSessions);
  const { filters, setFilters, filterOptions, filteredSessions } = useFilters(
    processedSessions,
    userProfiles
  );
  const { exportHandlers } = useExport(filteredSessions, hourlyPeakData);

  const paginatedSessions = useMemo(() => {
    const startIdx = (currentPage - 1) * itemsPerPage;
    return filteredSessions.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredSessions, currentPage, itemsPerPage]);

  useEffect(() => setCurrentPage(1), [filters, itemsPerPage]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleDateRangeChange = useCallback((field, value) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: { ...prev.dateRange, [field]: value },
    }));
  }, []);

  const clearFilters = useCallback(
    () => setFilters(CONFIG.INITIAL_FILTERS),
    []
  );

  const handleExport = useCallback(
    (e, { value }) => {
      if (exportHandlers[value]) {
        exportHandlers[value]();
      }
      setExportValue(null);
    },
    [exportHandlers]
  );

  const exportOptions = useMemo(
    () => [
      {
        key: CONFIG.EXPORT_TYPES.PDF,
        text: "Export as PDF",
        value: CONFIG.EXPORT_TYPES.PDF,
        icon: "file pdf outline",
      },
      {
        key: CONFIG.EXPORT_TYPES.EXCEL,
        text: "Export as Excel",
        value: CONFIG.EXPORT_TYPES.EXCEL,
        icon: "file excel outline",
      },
      {
        key: CONFIG.EXPORT_TYPES.CSV,
        text: "Export as CSV",
        value: CONFIG.EXPORT_TYPES.CSV,
        icon: "file outline",
      },
    ],
    []
  );

  const renderFilterField = (label, type, options = null, placeholder = "") => {
    const fieldProps = { style: { marginBottom: "15px" } };
    if (type === CONFIG.FILTER_TYPES.SEARCH_TERM) {
      return (
        <Form.Field {...fieldProps}>
          <label>{label}</label>
          <Input
            icon="search"
            placeholder={placeholder}
            value={filters.searchTerm}
            onChange={(e) =>
              handleFilterChange(
                CONFIG.FILTER_TYPES.SEARCH_TERM,
                e.target.value
              )
            }
          />
        </Form.Field>
      );
    }
    if (type === CONFIG.FILTER_TYPES.DATE_RANGE) {
      return (
        <>
          <Form.Field {...fieldProps}>
            <label>Start Date</label>
            <Input
              type="date"
              value={filters.dateRange.start}
              onChange={(e) => handleDateRangeChange("start", e.target.value)}
            />
          </Form.Field>
          <Form.Field {...fieldProps}>
            <label>End Date</label>
            <Input
              type="date"
              value={filters.dateRange.end}
              onChange={(e) => handleDateRangeChange("end", e.target.value)}
            />
          </Form.Field>
        </>
      );
    }
    return (
      <Form.Field {...fieldProps}>
        <label>{label}</label>
        <Dropdown
          placeholder={placeholder}
          fluid
          clearable
          selection
          options={options}
          value={filters[type]}
          onChange={(e, { value }) => handleFilterChange(type, value)}
        />
      </Form.Field>
    );
  };

  // Define fixed widths for each column
  const columnWidths = [
    "150px",
    "80px",
    "120px",
    "100px",
    "100px",
    "100px",
    "100px",
    "120px",
  ];

  const styles = {
    segment: {
      padding: "30px",
      borderRadius: "10px",
      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    },
    header: {
      textAlign: "center",
      color: "#225745",
      marginBottom: "30px",
      fontWeight: "400",
    },
    headerCell: {
      backgroundColor: "#225745",
      color: "white",
      padding: "12px",
      fontSize: "0.9em",
    },
    cell: {
      padding: "12px",
      fontSize: "0.85em",
      wordWrap: "break-word",
      whiteSpace: "normal",
    },
    summary: {
      marginTop: "15px",
      textAlign: "center",
      color: "#7f8c8d",
      fontWeight: "bold",
    },
  };

  return (
    <Segment style={styles.segment}>
      <Header as="h3" style={styles.header}>
        <Icon name="history" />
        Complete Session History
        <Header.Subheader>
          All check-in and check-out records with purpose of visit and user
          profiles
        </Header.Subheader>
      </Header>
      {loadingProfiles && (
        <Message info>
          <Loader active inline size="small" />
          Loading user profiles...
        </Message>
      )}
      <Grid>
        <Grid.Column width={4}>
          <Segment style={{ backgroundColor: "#f8f9fa" }}>
            <Header as="h4">
              <Icon name="filter" />
              Filters
            </Header>
            <Form>
              {renderFilterField(
                "Search",
                CONFIG.FILTER_TYPES.SEARCH_TERM,
                null,
                "Search sessions, users..."
              )}
              {renderFilterField(
                "Program",
                CONFIG.FILTER_TYPES.PROGRAM,
                filterOptions.programs,
                "All Programs"
              )}
              {renderFilterField(
                "Year",
                CONFIG.FILTER_TYPES.YEAR,
                filterOptions.years,
                "All Years"
              )}
              {renderFilterField(
                "Purpose of Visit",
                CONFIG.FILTER_TYPES.PURPOSE,
                filterOptions.purposes,
                "All Purposes"
              )}
              {renderFilterField(
                "Status",
                CONFIG.FILTER_TYPES.STATUS,
                filterOptions.status,
                "All Status"
              )}
              {renderFilterField("Date Range", CONFIG.FILTER_TYPES.DATE_RANGE)}
              <Button
                fluid
                basic
                color="grey"
                onClick={clearFilters}
                icon="refresh"
                content="Clear All"
              />
            </Form>
          </Segment>
        </Grid.Column>
        <Grid.Column width={12}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ marginBottom: "10px" }}>
              <span style={{ marginRight: "10px", fontWeight: "bold" }}>
                Rows per page:
              </span>
              <Dropdown
                selection
                compact
                options={CONFIG.PAGINATION_OPTIONS}
                value={itemsPerPage}
                onChange={(e, { value }) => setItemsPerPage(value)}
              />
            </div>
            <div style={{ marginBottom: "20px" }}>
              <Button.Group color="green">
                <Button
                  icon="download"
                  content="Export"
                  onClick={(e) => e.preventDefault()}
                />
                <Dropdown
                  className="button icon"
                  floating
                  options={exportOptions}
                  value={exportValue}
                  onChange={handleExport}
                  trigger={<></>}
                  selectOnBlur={false}
                  selectOnNavigation={false}
                />
              </Button.Group>
            </div>
          </div>

          {/* Fixed Table (non-scrollable) */}
          <Table
            celled
            striped
            selectable
            size="small"
            style={{ tableLayout: "fixed", minWidth: "100%" }}
          >
            <Table.Header>
              <Table.Row>
                {CONFIG.TABLE_HEADERS.map((header, i) => (
                  <Table.HeaderCell
                    key={i}
                    style={{
                      ...styles.headerCell,
                      width: columnWidths[i],
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                    }}
                  >
                    {header}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {filteredSessions.length > 0 ? (
                paginatedSessions.map((session, idx) => (
                  <Table.Row key={`${session.id}-${idx}`}>
                    <Table.Cell
                      style={{ ...styles.cell, width: columnWidths[0] }}
                    >
                      <div>
                        <strong style={{ color: "#2c3e50" }}>
                          {session.fullName || session.userName}
                        </strong>
                        {session.userProfile && (
                          <div style={{ fontSize: "0.8em", color: "#666" }}>
                            {session.department && `${session.department}`}
                            {session.position && ` - ${session.position}`}
                          </div>
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell
                      style={{ ...styles.cell, width: columnWidths[1] }}
                    >
                      <Label color="blue" size="medium">
                        {session.libraryId}
                      </Label>
                    </Table.Cell>
                    <Table.Cell
                      style={{ ...styles.cell, width: columnWidths[2] }}
                    >
                      {session.program} / {session.year}
                    </Table.Cell>
                    <Table.Cell
                      style={{ ...styles.cell, width: columnWidths[3] }}
                    >
                      <Label color="teal" size="small">
                        {session.purposeOfVisit}
                      </Label>
                    </Table.Cell>
                    <Table.Cell
                      style={{ ...styles.cell, width: columnWidths[4] }}
                    >
                      {session.checkInTime}
                    </Table.Cell>
                    <Table.Cell
                      style={{ ...styles.cell, width: columnWidths[5] }}
                    >
                      {session.isActive ? (
                        <em style={{ color: "#27ae60" }}>Still active</em>
                      ) : (
                        session.checkOutTime
                      )}
                    </Table.Cell>
                    <Table.Cell
                      style={{ ...styles.cell, width: columnWidths[6] }}
                    >
                      <strong style={{ color: "#16a085" }}>
                        {session.duration}
                      </strong>
                    </Table.Cell>
                    <Table.Cell
                      style={{ ...styles.cell, width: columnWidths[7] }}
                    >
                      <Label
                        color={session.isActive ? "green" : "grey"}
                        size="medium"
                      >
                        {session.isActive ? "Active" : "Completed"}
                      </Label>
                    </Table.Cell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.Cell
                    colSpan="8"
                    textAlign="center"
                    style={{ padding: "30px" }}
                  >
                    <Message info size="large">
                      <Message.Header>
                        {processedSessions.length === 0
                          ? "No Sessions Found"
                          : "No Results Match Your Filters"}
                      </Message.Header>
                      <p>
                        {processedSessions.length === 0
                          ? "No attendance sessions available."
                          : "Try adjusting your filters to see more results."}
                      </p>
                    </Message>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>

          {filteredSessions.length > 0 && (
            <>
              <div style={styles.summary}>
                Showing {paginatedSessions.length} of {filteredSessions.length}{" "}
                filtered sessions (page {currentPage} of{" "}
                {Math.ceil(filteredSessions.length / itemsPerPage)}) -{" "}
                {new Set(filteredSessions.map((s) => s.userName)).size} unique
                users
              </div>
              <div style={{ marginTop: "20px", textAlign: "center" }}>
                <Pagination
                  activePage={currentPage}
                  onPageChange={(e, { activePage }) =>
                    setCurrentPage(activePage)
                  }
                  totalPages={Math.ceil(filteredSessions.length / itemsPerPage)}
                  siblingRange={2}
                  boundaryRange={2}
                  ellipsisItem={{ content: "...", icon: true }}
                  firstItem={{ content: "", icon: "angle double left" }}
                  lastItem={{ content: "", icon: "angle double right" }}
                  prevItem={{ content: "", icon: "angle left" }}
                  nextItem={{ content: "", icon: "angle right" }}
                />
              </div>
            </>
          )}
        </Grid.Column>
      </Grid>
    </Segment>
  );
};

UserSessionTable.propTypes = {
  allSessions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      userName: PropTypes.string,
      libraryId: PropTypes.string,
      program: PropTypes.string,
      year: PropTypes.string,
      purposeOfVisit: PropTypes.string,
      inTime: PropTypes.string,
      outTime: PropTypes.string,
    })
  ).isRequired,
  hourlyPeakData: PropTypes.shape({
    peakHour: PropTypes.string,
    peakCount: PropTypes.number,
    hourlyStats: PropTypes.object,
  }),
};

UserSessionTable.defaultProps = {
  hourlyPeakData: { peakHour: null, peakCount: 0, hourlyStats: {} },
};

export default UserSessionTable;
